import * as React from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { type Editor } from "@tiptap/react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"
import { useIsMobile } from "../../../hooks/use-mobile"

// --- Lib ---
import {
  isExtensionAvailable,
  isNodeTypeSelected,
  sanitizeUrl,
} from "../../../lib/tiptap-utils"

// --- Icons ---
import { ArrowDownToLineIcon } from "../../../components/tiptap-icons/arrow-down-to-line-icon"

export const MEDIA_DOWNLOAD_SHORTCUT_KEY = "mod+shift+d"

/**
 * Extracts file extension from URL or content type
 */
function getFileExtension(url: string, contentType?: string): string {
  const urlMatch = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)
  if (urlMatch && urlMatch[1]) {
    return `.${urlMatch[1].toLowerCase()}`
  }

  if (contentType) {
    const mimeMap: Record<string, string> = {
      // Image formats
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "image/svg+xml": ".svg",
      "image/bmp": ".bmp",
      // Video formats
      "video/mp4": ".mp4",
      "video/webm": ".webm",
      "video/ogg": ".ogg",
      "video/avi": ".avi",
      "video/mov": ".mov",
      "video/quicktime": ".mov",
      "video/x-msvideo": ".avi",
      "video/3gpp": ".3gp",
      "video/x-flv": ".flv",
    }
    return mimeMap[contentType.toLowerCase()] || (contentType.startsWith('video/') ? ".mp4" : ".jpg")
  }

  return ".jpg"
}

/**
 * Configuration for the media download functionality
 */
export interface UseMediaDownloadConfig {
  /**
   * The Tiptap editor instance.
   */
  editor?: Editor | null
  /**
   * Whether the button should hide when download is not available.
   * @default false
   */
  hideWhenUnavailable?: boolean
  /**
   * Callback function called after a successful media download.
   */
  onDownloaded?: (filename?: string) => void
  /**
   * Optional function to resolve file URLs before downloading.
   * Useful for handling relative paths or custom URL schemes.
   */
  resolveFileUrl?: (url: string) => Promise<string>
  /**
   * Download behavior: 'download' forces download, 'open' opens in new tab, 'auto' tries download with fallback
   * @default 'auto'
   */
  downloadMethod?: "download" | "open" | "auto"
}

/**
 * Checks if media can be downloaded in the current editor state
 */
export function canDownloadMedia(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, ["image", "video"])) return false

  return isNodeTypeSelected(editor, ["image", "video"])
}

/**
 * Gets the currently selected media data
 */
export function getSelectedMediaData(editor: Editor | null): {
  src?: string
  alt?: string
  title?: string
  nodeType?: string
} | null {
  if (!editor || !canDownloadMedia(editor)) return null

  const { selection } = editor.state

  if (selection instanceof NodeSelection) {
    const node = selection.node

    if (node.type.name === "image" || node.type.name === "video") {
      return {
        src: node.attrs.src,
        alt: node.attrs.alt,
        title: node.attrs.title,
        nodeType: node.type.name,
      }
    }
  }

  return null
}

/**
 * Attempts to download media via fetch (handles CORS)
 */
async function tryFetchDownload(
  url: string,
  filename: string
): Promise<boolean> {
  try {
    const response = await fetch(url)
    if (!response.ok) return false

    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)

    const hasExtension = /\.[a-zA-Z0-9]+$/.test(filename)
    const finalFilename = hasExtension
      ? filename
      : filename +
        getFileExtension(url, response.headers.get("content-type") || undefined)

    const link = document.createElement("a")
    link.href = blobUrl
    link.download = finalFilename
    link.style.display = "none"

    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(blobUrl)
    return true
  } catch (error) {
    console.warn("Fetch download failed:", error)
    return false
  }
}

/**
 * Downloads media via direct link (for same-origin or data URLs)
 */
function tryDirectDownload(url: string, filename: string): boolean {
  try {
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(filename)
    const finalFilename = hasExtension
      ? filename
      : filename + getFileExtension(url)

    const link = document.createElement("a")
    link.href = url
    link.target = "_blank"
    link.download = finalFilename
    link.style.display = "none"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return true
  } catch (error) {
    console.warn("Direct download failed:", error)
    return false
  }
}

/**
 * Opens media in new tab
 */
function openInNewTab(url: string): boolean {
  try {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer")
    if (newWindow) {
      newWindow.focus()
      return true
    }
    return false
  } catch (error) {
    console.error("Failed to open media:", error)
    return false
  }
}

/**
 * Downloads the currently selected media
 */
export async function downloadSelectedMedia(
  editor: Editor | null,
  filename?: string,
  resolveFileUrl?: (url: string) => Promise<string>,
  downloadMethod: "download" | "open" | "auto" = "auto"
): Promise<boolean> {
  if (!editor || !canDownloadMedia(editor)) return false

  const mediaData = getSelectedMediaData(editor)
  if (!mediaData?.src) return false

  try {
    let resolvedUrl = mediaData.src
    if (resolveFileUrl) {
      resolvedUrl = await resolveFileUrl(mediaData.src)
    }

    const baseUrl = window.location.href
    const sanitizedUrl = sanitizeUrl(resolvedUrl, baseUrl)

    if (sanitizedUrl === "#") {
      console.error("Invalid or unsafe URL after sanitization")
      return false
    }

    const isVideo = mediaData.nodeType === "video"
    const defaultPrefix = isVideo ? "video" : "image"
    const generatedFilename =
      filename || mediaData.alt || mediaData.title || `${defaultPrefix}-${Date.now()}`

    switch (downloadMethod) {
      case "open":
        return openInNewTab(sanitizedUrl)

      case "download":
        if (
          sanitizedUrl.startsWith(window.location.origin) ||
          sanitizedUrl.startsWith("data:")
        ) {
          return tryDirectDownload(sanitizedUrl, generatedFilename)
        } else {
          const success = await tryFetchDownload(
            sanitizedUrl,
            generatedFilename
          )
          return success || openInNewTab(sanitizedUrl)
        }

      case "auto":
      default:
        if (
          sanitizedUrl.startsWith("data:") ||
          sanitizedUrl.startsWith(window.location.origin)
        ) {
          const directSuccess = tryDirectDownload(sanitizedUrl, generatedFilename)
          if (directSuccess) return true
        }
        
        // Try fetch download for external URLs
        const fetchSuccess = await tryFetchDownload(
          sanitizedUrl,
          generatedFilename
        )
        if (fetchSuccess) return true

        return openInNewTab(sanitizedUrl)
    }
  } catch (error) {
    console.error("Failed to download media:", error)

    try {
      const baseUrl = window.location.href
      const sanitizedUrl = sanitizeUrl(mediaData.src, baseUrl)
      if (sanitizedUrl !== "#") {
        return openInNewTab(sanitizedUrl)
      }
    } catch {
      // Silent fail
    }

    return false
  }
}

/**
 * Determines if the download button should be shown
 */
export function shouldShowDownloadButton(props: {
  editor: Editor | null
  hideWhenUnavailable: boolean
}): boolean {
  const { editor, hideWhenUnavailable } = props

  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, ["image", "video", "imageUpload"])) return false

  if (hideWhenUnavailable) {
    return canDownloadMedia(editor)
  }

  return true
}

/**
 * Custom hook that provides media download functionality for Tiptap editor
 */
export function useMediaDownload(config?: UseMediaDownloadConfig) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onDownloaded,
    resolveFileUrl,
    downloadMethod = "auto",
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsMobile()
  const [isVisible, setIsVisible] = React.useState(true)
  const canDownload = canDownloadMedia(editor)

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowDownloadButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    }
  }, [editor, hideWhenUnavailable])

  const handleDownload = React.useCallback(async () => {
    if (!editor) return false

    const mediaData = getSelectedMediaData(editor)
    const filename = mediaData?.alt || mediaData?.title

    const success = await downloadSelectedMedia(
      editor,
      filename,
      resolveFileUrl,
      downloadMethod
    )
    if (success) {
      onDownloaded?.(filename)
    }
    return success
  }, [editor, onDownloaded, resolveFileUrl, downloadMethod])

  useHotkeys(
    MEDIA_DOWNLOAD_SHORTCUT_KEY,
    (event) => {
      event.preventDefault()
      handleDownload()
    },
    {
      enabled: isVisible && canDownload,
      enableOnContentEditable: !isMobile,
      enableOnFormTags: true,
    }
  )

  const selectedMediaData = getSelectedMediaData(editor)
  const isVideo = selectedMediaData?.nodeType === "video"

  return {
    isVisible,
    canDownload,
    handleDownload,
    label: `Download ${isVideo ? "video" : "image"}`,
    shortcutKeys: MEDIA_DOWNLOAD_SHORTCUT_KEY,
    Icon: ArrowDownToLineIcon,
  }
}

// Backward compatibility aliases
export const IMAGE_DOWNLOAD_SHORTCUT_KEY = MEDIA_DOWNLOAD_SHORTCUT_KEY
export type UseImageDownloadConfig = UseMediaDownloadConfig
export const canDownloadImage = canDownloadMedia
export const getSelectedImageData = getSelectedMediaData
export const downloadSelectedImage = downloadSelectedMedia
export const useImageDownload = useMediaDownload
